import React from "react";
import { Link } from "react-router-dom";
import { capabilities, company, milestones, stats } from "../data/companyData";
import Reveal from "../components/Reveal";
import Counter from "../components/Counter";
import Parallax from "../components/Parallax";
import Seo from "../components/Seo";
import { breadcrumbSchema, pageMeta } from "../data/seoData";
import "./About.css";

const REGISTRY = [
  { label: "Proprietor", value: company.ceo },
  { label: "Year established", value: String(company.established) },
  { label: "Legal status", value: company.legalStatus },
  { label: "Nature of business", value: company.business },
  { label: "GST number", value: company.gst },
  { label: "Registered office", value: `${company.address.city}, ${company.address.state}` },
  { label: "Banking partners", value: company.banks.join(" · ") },
];

const SERVICES = [
  "Import & export of industrial chemicals",
  "Wholesale distribution and part-load supply",
  "Custom procurement against specification",
  "Quality testing & certification",
  "Logistics and supply-chain management",
];

const About = () => (
  <>
    <Seo
      {...pageMeta.about}
      schemas={[["breadcrumb", breadcrumbSchema([{ name: "About", path: "/about" }])]]}
    />
    <header className="phero">
      <div className="phero__glow" />
      <div className="phero__grid" />
      <div className="shell phero__inner">
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <span>About</span>
        </nav>

        <Reveal as="h1" className="phero__title" variant="mask">
          A quarter century in the import trade.
        </Reveal>
        <Reveal as="p" className="phero__lead" delay={0.1}>
          {company.name} began in {company.established} as a Mumbai
          proprietorship trading industrial salts. Today it imports eleven
          compounds directly and supplies more than 500 industrial clients
          across India.
        </Reveal>
      </div>
    </header>

    {/* ================================================== Story =========== */}
    <section className="section on-light">
      <div className="shell split">
        <div className="split__sticky">
          <Reveal as="p" className="eyebrow">Our story</Reveal>
          <Reveal as="h2" className="sec-head__title" delay={0.05}>
            Built on origin relationships, not middlemen.
          </Reveal>
        </div>

        <div className="prose">
          <Reveal as="p" className="prose__lead">
            We started where most Indian chemical traders start, buying from
            local stockists and reselling. It worked, but it meant we could
            never answer the one question customers actually cared about:
            where did this batch come from?
          </Reveal>
          <Reveal as="p" delay={0.05}>
            So we went upstream. Direct manufacturer relationships mean grade
            and purity are agreed at origin, documented on shipment and verified
            on arrival. When a customer asks for 99.5% potassium bicarbonate,
            they get 99.5%, with the certificate to prove it.
          </Reveal>
          <Reveal as="p" delay={0.1}>
            That discipline is the whole business. It is why glass manufacturers,
            fertilizer blenders, feed producers and pharmaceutical processors
            keep coming back, and why our catalogue has stayed deliberately
            narrow: eleven compounds we know completely, rather than a hundred
            we half know.
          </Reveal>

          <Reveal className="prose__mission" delay={0.15}>
            <p className="eyebrow">Mission</p>
            <p>
              To be the preferred partner for businesses sourcing high-quality
              industrial chemicals, providing solutions that let our customers
              plan production with confidence.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    {/* ================================================== Stats =========== */}
    <section className="section--tight on-light" aria-label="Company figures">
      <div className="shell">
        <Reveal className="stats">
          {stats.map((s) => (
            <div className="stat" key={s.label}>
              <div className="stat__value">
                <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} />
              </div>
              <div className="stat__label">{s.label}</div>
              <div className="stat__detail">{s.detail}</div>
            </div>
          ))}
        </Reveal>
      </div>
    </section>

    {/* =============================================== Timeline =========== */}
    <section className="section on-dark">
      <div className="shell">
        <div className="sec-head">
          <div>
            <Reveal as="p" className="eyebrow eyebrow--dark">Timeline</Reveal>
            <Reveal as="h2" className="sec-head__title" delay={0.05}>
              How the business grew
            </Reveal>
          </div>
        </div>

        <ol className="tline">
          {milestones.map((m, i) => (
            <Reveal as="li" className="tline__item" key={m.year} delay={i * 0.06}>
              <span className="tline__year mono">{m.year}</span>
              <span className="tline__dot" aria-hidden="true" />
              <div className="tline__text">
                <h3>{m.title}</h3>
                <p>{m.body}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>

    {/* ============================================ Statement ============= */}
    <Parallax src="/images/bags-warehouse.jpg" alt="" className="statement">
      <div className="statement__wash" />
      <div className="shell statement__inner">
        <Reveal as="p" className="statement__quote" variant="mask">
          Eleven compounds we know <em className="editorial">completely</em>.
        </Reveal>
        <Reveal as="p" className="statement__by" delay={0.2}>
          Mumbai warehouse · 25 &amp; 50 kg packing
        </Reveal>
      </div>
    </Parallax>

    {/* ============================================ Capabilities ========== */}
    <section className="section on-light">
      <div className="shell split">
        <div className="split__sticky">
          <Reveal as="p" className="eyebrow">Why choose us</Reveal>
          <Reveal as="h2" className="sec-head__title" delay={0.05}>
            What you get, every order.
          </Reveal>
        </div>
        <div className="rows">
          {capabilities.map((c, i) => (
            <Reveal className="row" key={c.index} delay={i * 0.06}>
              <div className="row__index">{c.index}</div>
              <h3 className="row__title">{c.title}</h3>
              <p className="row__body">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* ======================================== Registry + services ======= */}
    <section className="section on-sand-1">
      <div className="shell about__two">
        <div>
          <Reveal as="p" className="eyebrow">Company information</Reveal>
          <dl className="registry">
            {REGISTRY.map((r, i) => (
              <Reveal className="registry__row" key={r.label} delay={i * 0.04}>
                <dt>{r.label}</dt>
                <dd>{r.value}</dd>
              </Reveal>
            ))}
          </dl>
        </div>

        <div>
          <Reveal as="p" className="eyebrow">Services</Reveal>
          <ul className="svc">
            {SERVICES.map((s, i) => (
              <Reveal as="li" className="svc__item" key={s} delay={i * 0.05}>
                <span className="svc__num mono">{String(i + 1).padStart(2, "0")}</span>
                {s}
              </Reveal>
            ))}
          </ul>

          <Reveal className="about__payments" delay={0.2}>
            <p className="eyebrow">Accepted payment</p>
            <div className="about__chips">
              {company.payments.map((p) => (
                <span className="chip" key={p}>{p}</span>
              ))}
            </div>
            <p className="eyebrow" style={{ marginTop: "1.5rem" }}>Dispatch modes</p>
            <div className="about__chips">
              {company.shipping.map((s) => (
                <span className="chip" key={s}>{s}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* =================================================== CTA ============ */}
    <section className="cta-band">
      <div className="cta-band__glow" />
      <div className="shell cta-band__inner">
        <div>
          <p className="eyebrow eyebrow--dark">Work with us</p>
          <h2 className="cta-band__title">
            Twenty-five years of supply, one conversation away.
          </h2>
        </div>
        <div>
          <div className="cta-band__actions">
            <Link to="/contact" className="btn btn--accent btn--lg">
              Get in touch
              <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
            <Link to="/products" className="btn btn--ghost-light btn--lg">
              See the catalogue
            </Link>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default About;
